import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import Link from 'react-router-dom/Link';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FitbitIcon from '@mui/icons-material/Fitbit';
import GamepadIcon from '@mui/icons-material/Gamepad';

function WorkIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        // Token is not expired, fetch data
        fetchtableData();
    }, [isAuthenticated, companyId, branchId]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchtableData = async () => {
        setLoading(true);
        fetchData('WorkApi', `/${companyId}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    console.log(response);
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Work Details:', error);
            })
            .finally(() => {
                setLoading(false); // Hide loading screen
            });
    };

    const handleDelete = async () => {
        deleteData('WorkApi', `${deleteItemId}`, token)//, token
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('WorkApi', `${itemId}`)//, token
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-25px" }}>
            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px" }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Work
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

                {hasCreateMasterPermission && (
                    <Tooltip title="Create New Work" arrow>
                        <Button
                            component={Link}
                            to="/WorkCreate"
                            style={{
                                background: "#3F4F44",
                                color: "white",
                                fontSize: "12px",
                                border: "none",
                                borderRadius: "20px",
                                padding: "4px 14px",
                                cursor: "pointer",
                                transition: "0.2s",
                                fontFamily: 'inherit'
                            }}
                        >
                            Create
                        </Button>
                    </Tooltip>
                )}
            </div>

            <div className="watermark" >
                <CopyrightIcon /><b style={{ fontFamily: 'serif' }}>EFFTABS SOLUTIONS LLP</b>
            </div>

            <div className='mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#0f172a" }}> {/* Dark navy-blue */}
                                {[
                                    "Sl.No",
                                    "Name",
                                    "HSN Code",
                                    "GST %",
                                    "Work",
                                    "",
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            borderBottom: "2px solid #1e293b",
                                            color: "#f8fafc", // soft white
                                            padding: "10px 12px",
                                            textAlign: ["Work"].includes(head)
                                                ? "center"
                                                : "left",
                                            letterSpacing: "0.5px",
                                            backgroundColor: "#0f172a", // Dark navy-blue
                                            padding: '2px'
                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        
                        <TableBody>
                            {filteredData && filteredData.map((item, index) => (
                                <TableRow
                                    key={item.id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#F1E7E7' },
                                        transition: 'background-color 0.3s ease-in-out'
                                    }}
                                >
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', borderRight: '1px solid # #f4dfdfff' }}>{index + 1}</TableCell>
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', width: '50%', borderRight: '1px solid # #f4dfdfff' }}>{item.Name}</TableCell>
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', textAlign: 'center', borderRight: '1px solid # #f4dfdfff' }}>{item.HSNCode}</TableCell>
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', textAlign: 'right', borderRight: '1px solid  #f4dfdfff' }}>{Number(item.GSTRate)}</TableCell>
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', textAlign: 'right', borderRight: '1px solid  #f4dfdfff' }}>
                                        {/* {hasEditMasterPermission && ( */}
                                        <Tooltip title="Create Sub Work Item">
                                            <Link to={`/WorkItemCreate/${item.Id}`}>
                                                {/* <IconButton color="secondary">
                                                    <FitbitIcon style={{ fontSize: '21px', color: 'black' }} /> <span style={{ fontSize: '12px', color: 'black' }}>Work Item</span>
                                                </IconButton> */}
                                                <button
                                                    style={{
                                                        // backgroundColor: '#113F67',
                                                        backgroundColor: '#D4BEE4',
                                                        color: '#ffffff',
                                                        padding: '6px 12px',
                                                        fontSize: '13px',
                                                        height: '25px',
                                                        borderRadius: '6px',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.15s ease, background-color 0.2s ease',
                                                    }}
                                                >
                                                    <FitbitIcon style={{ fontSize: '15px', marginTop: '-5px', color: '#2f064dff' }} /><span style={{ fontSize: '12px', color: '#2f064dff', marginTop: '-5px' }}>Work Item</span>
                                                </button>
                                            </Link>
                                        </Tooltip>
                                        <Tooltip title="Create Sub Work">
                                            <Link to={`/WorkDetailsCreate/${item.Id}`}>
                                                <button
                                                    style={{
                                                        // backgroundColor: '#0F828C',
                                                        backgroundColor: '#D9EAFD',
                                                        color: '#ffffff',
                                                        padding: '6px 12px',
                                                        fontSize: '13px',
                                                        height: '25px',
                                                        borderRadius: '6px',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.15s ease, background-color 0.2s ease',
                                                        marginLeft: '5px'
                                                    }}
                                                >
                                                    <GamepadIcon style={{ fontSize: '15px', marginTop: '-5px', color: '#093362ff' }} /><span style={{ fontSize: '12px', color: '#093362ff', marginTop: '-5px' }}>Item Details</span>
                                                </button>
                                            </Link>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className='table-header-cell.child' sx={{ padding: '3px', textAlign: 'right', borderRight: '1px solid #f4dfdfff' }}>
                                        <Tooltip title="Edit Work">
                                            <Link to={`/WorkEdit/${item.Id}`}>
                                                {/* <IconButton color="primary">
                                                    <EditIcon style={{ fontSize: '19px' }} />
                                                </IconButton> */}
                                                <button
                                                    style={{
                                                        // backgroundColor: '#555879',
                                                        backgroundColor: '#DEE8CE',
                                                        color: '#ffffff',
                                                        padding: '6px 12px',
                                                        fontSize: '13px',
                                                        height: '25px',
                                                        borderRadius: '6px',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.15s ease, background-color 0.2s ease',
                                                        marginLeft: '5px'
                                                    }}
                                                >
                                                    <EditIcon style={{ fontSize: '15px', marginTop: '-5px', color: 'darkgreen' }} /><span style={{ fontSize: '12px', color: 'darkgreen', marginTop: '-5px' }}>Edit</span>
                                                </button>
                                            </Link>
                                        </Tooltip>
                                        {/* )} */}

                                        {/* {hasDeleteMasterPermission && ( */}
                                        <Tooltip title="Delete Work">
                                            <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} color="error">
                                                <button
                                                    style={{
                                                        // backgroundColor: '#8A0000', 
                                                        backgroundColor: '#F5CBCB',
                                                        color: '#ffffff',
                                                        padding: '6px 12px',
                                                        fontSize: '13px',
                                                        height: '25px',
                                                        borderRadius: '6px',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.15s ease, background-color 0.2s ease',
                                                        marginLeft: '5px'
                                                    }}
                                                >
                                                    <DeleteIcon style={{ fontSize: '15px', marginTop: '-5px', color: '#821818ff' }} /> <span style={{ fontSize: '12px', color: '#6e0d0dff', marginTop: '-5px' }}> Delete</span>
                                                </button>
                                            </IconButton>
                                        </Tooltip>
                                        {/* )} */}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button onClick={closeConfirmDialog} className="bg-red-600 text-white border border-red-600 rounded px-4 py-2 mr-2">
                                    Cancel
                                </button>
                                <button onClick={handleDelete} className="bg-green-600 text-white border border-green-600 rounded px-4 py-2">
                                    Confirm
                                </button>
                            </DialogActions>
                        </Dialog>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default WorkIndex;

